import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { FALLBACK_LANGUAGE, NAMESPACES, resources, type LanguageCode } from './locales';
import { LANGUAGE_KEY, readLanguagePref, resolveLanguage, type LanguagePref } from './lib/language';

function systemLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  return navigator.languages?.length ? navigator.languages : navigator.language ? [navigator.language] : [];
}

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

// Выбор держим и в памяти: при недоступном localStorage он всё равно действует в этой сессии.
let currentPref: LanguagePref = readLanguagePref(storage());

export function getLanguagePref(): LanguagePref {
  return currentPref;
}

function applyDocumentLanguage(code: LanguageCode) {
  if (typeof document !== 'undefined') document.documentElement.lang = code;
}

/** Сохранить выбор («system» убирает ключ) и сразу переключить интерфейс. */
export async function setLanguagePref(pref: LanguagePref): Promise<void> {
  currentPref = pref;
  try {
    if (pref === 'system') storage()?.removeItem(LANGUAGE_KEY);
    else storage()?.setItem(LANGUAGE_KEY, pref);
  } catch {
    /* хранилище недоступно — язык применится только на эту сессию */
  }
  const code = resolveLanguage(pref, systemLanguages());
  await i18n.changeLanguage(code);
}

const initial = resolveLanguage(getLanguagePref(), systemLanguages());

// document.lang следует за фактическим языком, даже если i18n.changeLanguage вызван напрямую.
i18n.on('languageChanged', (lng) => applyDocumentLanguage(lng as LanguageCode));

void i18n.use(initReactI18next).init({
  resources,
  lng: initial,
  fallbackLng: FALLBACK_LANGUAGE,
  ns: [...NAMESPACES],
  defaultNS: 'common',
  interpolation: { escapeValue: false }, // React сам экранирует
  returnNull: false,
});
applyDocumentLanguage(initial);

export default i18n;
